import { useEffect, useState } from 'react';
import {
  getAffaireContacts,
  getAffaireBiens,
  getAffaireDocuments,
} from '../api/expertise-detail.api';
import {
  Contact,
  Property,
  DocumentsData,
  UseAffaireDataReturn,
} from '../types/expertise-detail.types';

export const useAffaireData = (affaireId: string | undefined): UseAffaireDataReturn => {
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [biens, setBiens] = useState<Property[] | null>(null);
  const [documents, setDocuments] = useState<DocumentsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (!affaireId) {
      setLoading(false);
      setError('No affaire ID provided');
      return;
    }

    // Skip if already fetched for this affaireId
    if (hasFetched) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all data in parallel using Promise.allSettled to handle individual failures
        const [contactsResult, biensResult, documentsResult] = await Promise.allSettled([
          getAffaireContacts(affaireId),
          getAffaireBiens(affaireId),
          getAffaireDocuments(affaireId),
        ]);

        // Handle contacts
        if (contactsResult.status === 'fulfilled') {
          const contactsRes = contactsResult.value;
          if (contactsRes.success && contactsRes.data) {
            setContacts(contactsRes.data.contacts || []);
          } else {
            console.warn('Failed to fetch contacts:', contactsRes.message);
            setContacts([]);
          }
        } else {
          console.warn('Error fetching contacts:', contactsResult.reason);
          setContacts([]);
        }

        // Handle properties  
        if (biensResult.status === 'fulfilled') {
          const biensRes = biensResult.value;
          if (biensRes.success && biensRes.data) {
            setBiens(biensRes.data.biens || []);
          } else {
            console.warn('Failed to fetch properties:', biensRes.message);
            setBiens([]);
          }
        } else {
          console.warn('Error fetching properties:', biensResult.reason);
          setBiens([]);
        }

        // Handle documents
        if (documentsResult.status === 'fulfilled') {
          const documentsRes = documentsResult.value;
          if (documentsRes.success && documentsRes.data) {
            setDocuments(documentsRes.data);
          } else {
            console.warn('Failed to fetch documents:', documentsRes.message);
            setDocuments({
              requested_documents: { documents: [] },
              checked_documents: { documents: [] },
            });
          }
        } else {
          console.warn('Error fetching documents:', documentsResult.reason);
          setDocuments({
            requested_documents: { documents: [] },
            checked_documents: { documents: [] },
          });
        }

        setLoading(false);
        setHasFetched(true);
      } catch (err) {
        console.error('Error fetching affaire data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch affaire data');
        setLoading(false);
        setHasFetched(true);
        
        // Set empty data on error
        setContacts([]);
        setBiens([]);
        setDocuments({
          requested_documents: { documents: [] },
          checked_documents: { documents: [] },
        });
      }
    };

    fetchData();
  }, [affaireId]);

  // Reset hasFetched when affaireId changes
  useEffect(() => {
    setHasFetched(false);
  }, [affaireId]);

  return {
    contacts,
    biens,
    documents,
    loading,
    error,
  };
};